import {YourDealsFilterSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {FilterLabels} from '@aboutyou/legacy-tracking-sdk';
import {useEffect} from 'react';

export interface YourDealsInterface {
  activeFilters: Map<string, Array<string> | undefined>;
  data: YourDealsFilterSection;
  resetFilter: (slug: string) => void;
  resetFilters: (slug: string[]) => void;
  setFilter: (filterData: {filterId: string; filterSection: string}) => void;
  setFilters: (
    filterData: Array<{
      filterId: string;
      filterSection: string;
      label: FilterLabels;
      shouldTrackImmediately: boolean;
    }>,
  ) => void;
}

export const useYourDealsSection = ({
  activeFilters,
  data,
  resetFilter,
  resetFilters,
  setFilters,
}: Omit<YourDealsInterface, 'setFilter'>) => {
  const {sale, discount} = data;

  const isSaleFilterActive = activeFilters.get(sale.slug)?.[0] === 'true';
  const isDiscountFilterActive =
    activeFilters.get(discount?.slug || '')?.[0] === 'true';

  const saleFilter = {
    filter: {
      active: isSaleFilterActive,
      label: sale.headline,
      sublabel: sale.subline,
      slug: sale.slug,
    },
  };

  const filterSlug = discount?.slug ? discount?.slug : sale.slug;

  const discountFilter = discount
    ? {
        filter: {
          active: !isSaleFilterActive && isDiscountFilterActive,
          label: discount.headline,
          sublabel: discount.subline,
          slug: discount.slug,
          icon: discount.icon,
        },
      }
    : {filter: undefined};

  const onResetYourDealsFilter = () => {
    const discountSlug = discountFilter?.filter?.slug || '';
    resetFilters?.([discountSlug, saleFilter.filter.slug]);
  };

  const onToggleFilterContent = (slug: string, active: boolean) => {
    if (active) {
      resetFilter(slug);
    } else {
      const filtersToUpdate = [
        {
          filterId: String(!active),
          filterSection: slug,
          label: 'quick_filters' as FilterLabels,
          shouldTrackImmediately: true,
        },
      ];

      // Don't allow both filters to be ON at the same time
      if (slug === sale.slug && discountFilter.filter?.active) {
        filtersToUpdate.push({
          filterId: 'false',
          filterSection: discountFilter.filter?.slug || '',
          label: 'quick_filters' as FilterLabels,
          shouldTrackImmediately: true,
        });
      } else if (saleFilter.filter.active) {
        filtersToUpdate.push({
          filterId: 'false',
          filterSection: sale.slug,
          label: 'quick_filters' as FilterLabels,
          shouldTrackImmediately: true,
        });
      }

      setFilters(filtersToUpdate);
    }
  };

  useEffect(() => {
    // Prevent having both filters ON at the same time on the URL
    if (isSaleFilterActive && isDiscountFilterActive) {
      resetFilter(discount?.slug || '');
    }
  }, []);

  return {
    booleanFilters: [saleFilter, discountFilter],
    onToggleFilterContent,
    onResetYourDealsFilter,
    isSaleFilterActive,
    filterSlug,
  };
};
